import type {FC} from 'react'
import {useMemo} from 'react'
import {Box, Stack, Table, TableBody, TableCell, TableRow, Typography} from '@mui/material'

import {isTabularUnit, parseUnitContent} from '@shared/components/documentUnit/parseUnitContent'

/**
 * One extracted unit, rendered so a person can read it (AB#5992).
 *
 * A sheet arrives as delimited rows with the padding its widest row implies;
 * as monospace text that is a wall of commas. Back in a grid it is the table it
 * was. Prose stays prose — a PDF's paragraphs are not a table and forcing them
 * into one would invent structure the document does not have.
 */
const UnitContent: FC<{content: string; unit?: string}> = ({content, unit}) => {
  const blocks = useMemo(() => parseUnitContent(content, isTabularUnit(unit)), [content, unit])

  return (
    <Stack spacing={2}>
      {blocks.map((block, index) => (
        <Stack key={`${block.caption}-${index}`} spacing={0.5}>
          {block.caption && (
            <Typography variant="caption" color="text.secondary">
              {block.caption}
            </Typography>
          )}

          {block.kind === 'table' ? (
            // Its own scroller: a wide sheet must not push the modal sideways.
            <Box sx={{overflowX: 'auto', border: 1, borderColor: 'divider', borderRadius: 1}}>
              <Table size="small" sx={{'& td': {whiteSpace: 'nowrap'}}}>
                <TableBody>
                  {block.rows.map((row, rowIndex) => (
                    <TableRow key={rowIndex} hover>
                      {row.map((cell, cellIndex) => (
                        <TableCell key={cellIndex} sx={{fontSize: 12, py: 0.5}}>
                          {cell}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ) : (
            <Typography
              variant="body2"
              sx={{whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 13}}
            >
              {block.text}
            </Typography>
          )}
        </Stack>
      ))}
    </Stack>
  )
}

export default UnitContent
